import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { isProjectSellable } from "../projects/sellableProjects";
import type { MetaFormProject, Project } from "../types";
import {
  isMappingValid,
  normalizeFormId,
  toMetaFormErrorMessage,
  validateFormMapping,
  type MetaFormMappingErrors,
} from "./metaLeadsRules";

const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9";

/**
 * Almikia Epic E9 (TASK-049): inline editor of the Meta form id -> project mapping. Add and
 * change the project only: a mapping is never deleted, and only sellable projects are offered.
 * Direccion only (the screen's own gate, the provider guard and RLS).
 */
export const MetaFormProjectsEditor = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const mappings = useGetList<MetaFormProject>("meta_form_projects", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "id", order: "ASC" },
  });
  const projects = useGetList<Project>("projects", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<MetaFormProject["id"] | null>(
    null,
  );

  const rows = mappings.data ?? [];
  const allProjects = projects.data ?? [];
  const projectName = (id: MetaFormProject["project_id"]) =>
    allProjects.find((project) => String(project.id) === String(id))?.name ??
    String(id);

  const handleSave = async (
    values: { meta_form_id: string; project_id: string },
    mapping?: MetaFormProject,
  ) => {
    const data = {
      meta_form_id: normalizeFormId(values.meta_form_id),
      project_id: Number(values.project_id),
    };
    if (mapping) {
      await dataProvider.update("meta_form_projects", {
        id: mapping.id,
        data,
        previousData: mapping,
      });
      setEditingId(null);
      notify("crm.metaLeads.mapping.updated_notice");
    } else {
      await dataProvider.create("meta_form_projects", { data });
      setIsAdding(false);
      notify("crm.metaLeads.mapping.created_notice");
    }
    mappings.refetch();
  };

  let body;
  if (mappings.error) {
    body = <ListErrorState onRetry={() => mappings.refetch()} />;
  } else if (mappings.isPending || projects.isPending) {
    body = <ListCardsSkeleton rows={2} />;
  } else {
    body = (
      <>
        {isAdding ? (
          <MappingForm
            existing={rows}
            projects={allProjects}
            onSave={(values) => handleSave(values)}
            onCancel={() => setIsAdding(false)}
          />
        ) : null}
        {rows.length === 0 ? (
          <ListEmptyState title={translate("crm.metaLeads.mapping.empty")} />
        ) : (
          <div
            className="divide-y rounded-md border"
            data-testid="meta-form-projects-list"
          >
            {rows.map((mapping) =>
              editingId === mapping.id ? (
                <div key={mapping.id} className="p-3">
                  <MappingForm
                    existing={rows}
                    projects={allProjects}
                    mapping={mapping}
                    onSave={(values) => handleSave(values, mapping)}
                    onCancel={() => setEditingId(null)}
                  />
                </div>
              ) : (
                <div
                  key={mapping.id}
                  data-testid="meta-form-project-row"
                  className="flex flex-wrap items-center gap-3 p-3"
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="font-medium break-all">
                      {mapping.meta_form_id}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {projectName(mapping.project_id)}
                    </span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="min-h-11"
                    onClick={() => setEditingId(mapping.id)}
                  >
                    <Pencil className="size-4 mr-1" />
                    {translate("crm.metaLeads.mapping.change_action")}
                  </Button>
                </div>
              ),
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">
            {translate("crm.metaLeads.mapping.title")}
          </h2>
          {!isAdding ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="min-h-11"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="size-4 mr-1" />
              {translate("crm.metaLeads.mapping.add_action")}
            </Button>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          {translate("crm.metaLeads.mapping.intro")}
        </p>
        {body}
      </CardContent>
    </Card>
  );
};

/** Create/edit row: Meta form id (fixed once saved) and a sellable project. */
const MappingForm = ({
  existing,
  projects,
  mapping,
  onSave,
  onCancel,
}: {
  existing: readonly MetaFormProject[];
  projects: readonly Project[];
  mapping?: MetaFormProject;
  onSave: (values: {
    meta_form_id: string;
    project_id: string;
  }) => Promise<void>;
  onCancel: () => void;
}) => {
  const translate = useTranslate();
  const idPrefix = `meta-form-${mapping?.id ?? "new"}`;
  const [formId, setFormId] = useState(mapping?.meta_form_id ?? "");
  const [projectId, setProjectId] = useState(
    mapping ? String(mapping.project_id) : "",
  );
  const [errors, setErrors] = useState<MetaFormMappingErrors>({});
  const [saveError, setSaveError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  // Only sellable projects are offered; the current one stays visible while editing.
  const options = projects.filter(
    (project) => isProjectSellable(project) || String(project.id) === projectId,
  );

  const handleSave = async () => {
    const values = { meta_form_id: formId, project_id: projectId };
    const found = validateFormMapping(values, existing, mapping?.id);
    setErrors(found);
    setSaveError(undefined);
    if (!isMappingValid(found)) return;
    setIsSaving(true);
    try {
      await onSave(values);
    } catch (error) {
      setSaveError(toMetaFormErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border bg-muted/30 p-3">
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${idPrefix}-form-id`}>
          {translate("resources.meta_form_projects.fields.meta_form_id")}
        </Label>
        <Input
          id={`${idPrefix}-form-id`}
          value={formId}
          disabled={isSaving || mapping != null}
          aria-invalid={errors.meta_form_id != null}
          onChange={(event) => setFormId(event.target.value)}
        />
        {errors.meta_form_id ? (
          <p className="text-sm text-destructive">{errors.meta_form_id}</p>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${idPrefix}-project`}>
          {translate("resources.meta_form_projects.fields.project_id")}
        </Label>
        <select
          id={`${idPrefix}-project`}
          className={SELECT_CLASS}
          value={projectId}
          disabled={isSaving}
          aria-invalid={errors.project_id != null}
          onChange={(event) => setProjectId(event.target.value)}
        >
          <option value="">
            {translate("crm.metaLeads.mapping.choose_project")}
          </option>
          {options.map((project) => (
            <option key={project.id} value={String(project.id)}>
              {project.name}
            </option>
          ))}
        </select>
        {errors.project_id ? (
          <p className="text-sm text-destructive">{errors.project_id}</p>
        ) : null}
      </div>
      {saveError ? (
        <p className="text-sm text-destructive">{saveError}</p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="min-h-11"
          disabled={isSaving}
          onClick={onCancel}
        >
          {translate("ra.action.cancel")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="gradient"
          className="min-h-11"
          disabled={isSaving}
          onClick={handleSave}
        >
          {translate("ra.action.save")}
        </Button>
      </div>
    </div>
  );
};
